import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, getWorksheetInitialQuestions, additionalAmountInitialQuestion } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFirstNameMiddleInitial,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const isWorksheetFilled = () => (
    data.filing_as_head_of_household
      || data.number_of_children
  );

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const [totalNoOfAllowances, setTotalNoOfAllowances] = useState(data.total_number_of_allowances || 0);
  const [totalNoOfAllowancesError, setTotalNoOfAllowancesError] = useState(false);

  const [worksheetOpen, setWorksheetOpen] = useState(isWorksheetFilled() || false);
  const worksheetForm = useRef();

  const worksheetInitialQuestions = getWorksheetInitialQuestions(version);
  const worksheetQuestionWithAnswer = getInitialQuestions(worksheetInitialQuestions);
  const [worksheetQuestion, setWorksheetQuestion] = useState(worksheetQuestionWithAnswer);

  useEffect(() => {
    setWorksheetQuestion(worksheetQuestionWithAnswer);
  }, [version]);

  const [filingAsHeadOfHousehold, setFilingAsHeadOfHousehold] = useState(data.filing_as_head_of_household || false);

  const additionalAmountForm = useRef();
  const additionalAmountQuestionWithAnswer = getInitialQuestions(additionalAmountInitialQuestion);
  const [additionalAmountQuestion, setAdditionalAmountQuestion] = useState(additionalAmountQuestionWithAnswer);

  const [exemption, setExemption] = useState(data.exemption || '');
  const [exemptionError, setExemptionError] = useState(false);

  // to calculate total number of allowances
  const calculatedTotalAllowances = () => {
    const noChildren = worksheetQuestion[0].answer.answer.number_of_children || 0;
    if (filingAsHeadOfHousehold) {
      return parseInt(noChildren) + 2;
    }
    return parseInt(noChildren);
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowances = (isReviewStep) => (
    <Flex column>
      <Text fontSize="14px" fontWeight="600" mb="8px">
        Allowances
      </Text>
      <S.QuestionWrapper mb="10px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Total number of allowances you're claiming:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={worksheetOpen ? calculatedTotalAllowances() : totalNoOfAllowances}
            onChange={(e) => setTotalNoOfAllowances(e.target.value)}
            disabled={worksheetOpen || isReviewStep}
          />
        </Flex>
        {totalNoOfAllowancesError && (
          <Flex justifyContent="flex-start">
            <Text color="red">Please enter a valid response.</Text>
          </Flex>
        )}
      </S.QuestionWrapper>
      <CollapsibleQuestions
        title="If you need to calculate your allowances, use this worksheet."
        handleClick={() => {
          if (!isReviewStep) {
            setWorksheetOpen(!worksheetOpen);
          }
        }}
        open={worksheetOpen || isReviewStep}
      >
        {renderWorksheet(isReviewStep)}
      </CollapsibleQuestions>
      <FormConsumer
        questions={additionalAmountQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAmountQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAmountQuestion(questions);
        }}
        ref={additionalAmountForm}
      />
    </Flex>
  );

  const renderWorksheet = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={worksheetQuestion}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = worksheetQuestion.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWorksheetQuestion(questions);
        }}
        ref={worksheetForm}
      />
      <Flex column>
        <S.QuestionWrapper mb="20px">
          <Flex>
            <label>
              <Flex>
                <S.Input
                  style={{ height: '20px' }}
                  type="checkbox"
                  value={filingAsHeadOfHousehold}
                  onChange={(e) => setFilingAsHeadOfHousehold(e.target.checked)}
                  checked={filingAsHeadOfHousehold}
                  disabled={isReviewStep}
                />
                <Box ml={1} />
                Check this box if your filing status will be head of household on your tax return.
              </Flex>
            </label>
          </Flex>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }} isRequired={isExemptFromWithholding}>
        Exemption from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="checkbox"
              value={exemption}
              checked={exemption}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemption(e.target.checked);
              }}
            />
            <Box ml={2} />
            <Flex column>
              <Text>Check this box to claim exemption from withholding if you meet both of the following conditions:</Text>
              <Text>
                {'\u2022'}
                {' '}
                Last year you had no Idaho income tax liability, and
              </Text>
              <Text>
                {'\u2022'}
                {' '}
                This year you expect to have no Idaho income tax liability.
              </Text>
            </Flex>
          </Flex>
        </label>
        {exemptionError && (
          <Flex justifyContent="flex-start">
            <Text color="red">If you do not meet any of the conditions above, you are required to fill up the form.</Text>
          </Flex>
        )}
      </S.QuestionWrapper>
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderAllowances()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (!worksheetOpen) {
                if (!(/^\d*$/.test(totalNoOfAllowances))) {
                  err = true;
                  setTotalNoOfAllowancesError(true);
                } else {
                  setTotalNoOfAllowancesError(false);
                }
              } else if (worksheetForm && worksheetForm.current) {
                worksheetForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
              if (additionalAmountForm && additionalAmountForm.current) {
                additionalAmountForm.current.validate((error) => {
                  if (error) err = error;
                });
              }
            } else if (!exemption) {
              err = true;
              setExemptionError(true);
            } else {
              setExemptionError(false);
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let totalNoOfAllowancesTemp;
            let worksheetData = resetInitialQuestions(worksheetInitialQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let filingAsHeadOfHouseholdTemp = false;
            let additionalAmountData = resetInitialQuestions(additionalAmountInitialQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let exemptionTemp = false;
            if (!isExemptFromWithholding) {
              if (worksheetOpen) {
                totalNoOfAllowancesTemp = calculatedTotalAllowances().toString();
                worksheetData = worksheetQuestion.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                filingAsHeadOfHouseholdTemp = filingAsHeadOfHousehold;
              } else {
                totalNoOfAllowancesTemp = totalNoOfAllowances.toString();
              }
              additionalAmountData = additionalAmountQuestion.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            } else {
              exemptionTemp = exemption;
            }
            const allowancesData = {
              total_number_of_allowances: totalNoOfAllowancesTemp,
              filing_as_head_of_household: filingAsHeadOfHouseholdTemp,
            };
            const exemptionData = { exemption: exemptionTemp, is_exempt: isExemptFromWithholding };
            const data = {
              ...stepOneData, ...allowancesData, ...worksheetData, ...additionalAmountData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LegacyForm;
